import ErrorMessage from 'components/ErrorMessage';
import type { FC, ReactNode } from 'react';
import { Component } from 'react';
import { accountStore } from 'stores/useAccountStore';
import debounce from 'lodash/debounce';
import useToastsStore from 'stores/useToastsStore';
import { ToastMessageIdEnum } from 'stores/useToastsStore/toastMessages';
import { useNavigate } from 'react-router-dom';

type AppErrorType = {
  message: string;
  response?: {
    status: number;
  };
} & Error;

const isFullScreen = () => {
  const fullScreenElement =
    document.fullscreenElement ??
    document.webkitFullscreenElement ??
    document.mozFullScreenElement ??
    document.msFullscreenElement ??
    null;

  if (fullScreenElement === null) return false;
  return true;
};

const displayAccessDeniedToast = debounce(
  (err) => {
    if (err) {
      console.error(err);
      const { addToast } = useToastsStore.getState();
      addToast({
        type: 'danger',
        message: ToastMessageIdEnum.ACCESS_DENIED_ERROR,
        timeout: 5000,
      });
    }
  },
  5000,
  { leading: true, trailing: false }
);

type AppErrorBoundaryClassProps = {
  navigate: (path: string) => void;
  children: ReactNode;
};

class AppErrorBoundaryClass extends Component<
  AppErrorBoundaryClassProps,
  { error?: AppErrorType }
> {
  state: { error?: AppErrorType } = {};

  clearErrorState = (): void => {
    this.setState({ error: undefined });
  };

  private handleError(error: AppErrorType): void {
    const { response: { status } = {} } = error;
    if (status === 401) {
      accountStore.getState().clearUser();
      this.props.navigate('/login?feedback=expired');
      return;
    }
    if (error.cause === 'RATE_LIMITED') {
      return;
    }
    if (!isFullScreen()) {
      this.setState({ error });
    }
  }

  private rejectionHandler = (event: PromiseRejectionEvent): void => {
    this.handleError(event.reason as AppErrorType);
  };

  componentDidMount(): void {
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  componentWillUnmount(): void {
    window.removeEventListener('unhandledrejection', this.rejectionHandler);
  }

  componentDidCatch(error: AppErrorType): void {
    this.handleError(error);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      switch (error.cause) {
        case 'ACCESS_DENIED': {
          displayAccessDeniedToast(error);
          break;
        }
        default: {
          const { message, response: { status } = {} } = error;
          const stackTrace = error.stack;
          return (
            <ErrorMessage
              errorCode={status}
              stackTrace={stackTrace}
              message={message}
              clearState={this.clearErrorState}
            />
          );
        }
      }
    }
    return this.props.children;
  }
}

// Functional wrapper provides `navigate` from the router.
const AppErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  return <AppErrorBoundaryClass navigate={navigate}>{children}</AppErrorBoundaryClass>;
};

export default AppErrorBoundary;
